import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";
import { SEOImage } from "../../../../components/seo/SEOImage";

export const TeleprompterApresentacoesEN = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter for PowerPoint and Presentations: Professional Oratory
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Master your slides and win over your audience with total confidence. <strong>Invisibly synchronize your script</strong> with PowerPoint, Google Slides, or Keynote using PromptNinja.
        </p>

        <p className="text-slate-300 mb-8 leading-relaxed">
            In this definitive guide for competent speakers, we show how PromptNinja becomes your strategic partner for high-impact presentations. Learn how to read your script naturally, maintaining eye contact with the audience and avoiding the fear of 'going blank' at critical moments. Discover techniques to integrate the teleprompter with any slide software, ensuring you present with fluidity, authority, and professionalism, whether on a physical stage or in virtual meetings. Transform your public speaking and stand out with memorable presentations without hesitations.
        </p>

        <SEOImage
            slug="scripts-para-aulas"
            src="scripts-for-teacher-lessons.webp"
            alt="Teacher using teleprompter for online classes"
            caption="PromptNinja helps educators maintain pacing and eye contact during recorded or live classes."
            width={1200}
            height={675}
        />

        <div className="bg-blue-600/10 border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center mb-12 shadow-xl">
            <h3 className="text-2xl font-bold text-white mb-4">Present with TED-Style Confidence</h3>
            <p className="text-slate-300 mb-6 font-medium">
                Transform your slides into visual support and yourself into the star of the presentation.
                The <strong>PromptNinja</strong> invisibly synchronizes your script for the audience.
            </p>
            <a href="/?lang=en#app" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-blue-500/25">
                Start Presentation with PromptNinja
            </a>
            <p className="mt-4 text-sm text-slate-400 font-medium">TED-style confidence instantly.</p>
        </div>


        <div className="bg-gradient-to-r from-red-900/30 to-green-900/30 p-8 rounded-xl border border-slate-700 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">Presentation: Memorize vs PRO Teleprompter</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/70 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> Traditional Method (Memorize)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📚 <strong>Prep:</strong> 2 weeks memorizing script</li>
                        <li>🧠 <strong>Stress:</strong> Fear of forgetting critical statistic</li>
                        <li>😰 <strong>Mid-presentation:</strong> Blank on slide 15 = nerves</li>
                        <li>📄 <strong>Backup:</strong> Paper on desk (look DOWN = lose contact)</li>
                        <li>⏱️ <strong>Result:</strong> Seems insecure, frequent "umm"</li>
                    </ul>
                </div>
                <div className="bg-slate-900/70 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> PromptNinja + PowerPoint
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>📚 <strong>Prep:</strong> 10 minutes pasting script</li>
                        <li>🧠 <strong>Stress:</strong> Zero (script always visible ONLY to you)</li>
                        <li>😌 <strong>Mid-presentation:</strong> Read fluently = confident</li>
                        <li>📱 <strong>Backup:</strong> Laptop/tablet only YOU see</li>
                        <li>⏱️ <strong>Result:</strong> "Masters subject", zero hesitation</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Teleprompter = confident presenter WITHOUT months of memorization.
            </p>
        </div>

        <SEOImage
            slug="scripts-para-aulas"
            src="scripts-for-presentation-classes.webp"
            alt="Slide presentation with integrated teleprompter"
            caption="Synchronize your slides with your script and never lose your train of thought in your presentations."
            width={1200}
            height={675}
        />

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">3 Professional Presentation Setups</h3>
        <div className="space-y-6 mb-12">
            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-cyan-500">
                <h3 className="text-xl font-bold text-cyan-400 mb-3">Setup #1: In-Person Auditorium (Conference Room)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop at podium + projector showing PPT.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Projector:</strong> Shows PowerPoint fullscreen (audience sees slides)</li>
                    <li><strong>Laptop (you):</strong> "Presenter View" PowerPoint on one monitor</li>
                    <li><strong>Tablet beside:</strong> PromptNinja with complete script</li>
                    <li>Look at audience, read discreetly from tablet (beside laptop)</li>
                    <li>Audience sees: you looking "forward" (actually reading invisible tablet)</li>
                    <li>Use [SLIDE X] marker in script = know when to advance PPT</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal for:</strong> Investor pitches, board presentations, keynotes.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-400 mb-3">Setup #2: Online Zoom/Teams (Hybrid)</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> PC/Mac + webcam + online presentation.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Zoom:</strong> Share Screen → select ONLY PowerPoint (not "Entire Screen")</li>
                    <li><strong>Monitor 1:</strong> PowerPoint in Presenter Mode (slides + private notes)</li>
                    <li><strong>Monitor 2:</strong> PromptNinja fullscreen BELOW webcam</li>
                    <li>Zoom shows: PowerPoint. You see: PowerPoint + PromptNinja</li>
                    <li>Read from teleprompter looking "at webcam" = perfect eye contact</li>
                    <li>Online audience: only sees slides + your confident face</li>
                </ol>
                <p className="text-green-400 text-sm mt-3">✅ <strong>Ideal for:</strong> Webinars, client meetings, corporate training online.</p>
            </div>

            <div className="bg-slate-800 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-red-400 mb-3">Setup #3: Google Slides + Phone Remote</h3>
                <p className="text-slate-300 mb-4">
                    <strong>Hardware:</strong> Laptop + phone (dual control).
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
                    <li><strong>Laptop:</strong> Google Slides presenting + PromptNinja side window</li>
                    <li><strong>Phone #1:</strong> Google Slides remote (advances slides)</li>
                    <li><strong>Phone #2:</strong> PromptNinja P2P remote (controls script scroll)</li>
                    <li>You control: slides with right hand, teleprompter with left</li>
                    <li>Perfect sync: advance slide = pause teleprompter = explain visual</li>
                    <li>Resume teleprompter = continue narrative</li>
                </ol>
                <p className="text-yellow-400 text-sm mt-3">⚠️ <strong>Pro tip:</strong> Use [STOP] command in script = auto pause at each slide.</p>
            </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg my-8 border-l-4 border-orange-500">
            <h3 className="text-2xl font-bold text-white mb-4">"Second Brain" Technique (Clean Slides + Rich Script)</h3>
            <p className="text-slate-300 mb-4">
                <strong>CLASSIC MISTAKE:</strong> Slides PACKED with text because you're afraid to forget. Audience reads slide = IGNORES you speaking.
            </p>
            <p className="text-slate-300 mb-4">
                <strong>PRO METHOD:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-300 text-sm">
                <li><strong>Slides:</strong> ONLY impactful images/graphs + title (max 10 words)</li>
                <li><strong>PromptNinja:</strong> COMPLETE script with statistics, arguments, transitions</li>
                <li>You speak: rich data from teleprompter. Audience sees: clean visual slide</li>
                <li>100% attention on YOU (not reading slide like robot)</li>
            </ul>
            <p className="text-green-400 text-sm mt-4">
                ✅ <strong>Result:</strong> TED-style presentation. Slides = visual support. You = star.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">How to Sync Slides with Script</h3>
        <div className="bg-slate-800 p-6 rounded-lg my-6">
            <SEOContentHowTo
                title=""
                schemaTitle="How to Sync Teleprompter and Slides"
                totalTime="PT5M"
                tools={["PromptNinja", "PowerPoint or Google Slides"]}
                steps={[
                    {
                        title: "Step 1: Structure Script by Slide",
                        text: "Copy the text from each slide into the script. Place [SLIDE 1], [SLIDE 2] at the beginning of each slide to know when to move on."
                    },
                    {
                        title: "Step 2: Navigate through the slides",
                        text: "You can navigate through the slides of your script using the arrow shortcuts: '→' to advance and '←' to go back."
                    },
                    {
                        title: "Step 3: Use STOP Commands for Pauses",
                        text: "Insert [STOP] after each slide marker. Teleprompter auto-pauses = you explain slide visual before continuing script. Or use the [PAUSE X] command, which allows you to pause for X seconds."
                    },
                    {
                        title: "Step 4: Rehearsal with Timer",
                        text: "First run-through: mark REAL time for each slide. Adjust script if any slide is too long/short. Goal: +/- 15% of planned."
                    }
                ]}
            />
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">5 Fatal Presentation Errors with Teleprompter</h3>
        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #1: Sharing ENTIRE Screen on Zoom (Exposes Teleprompter)</h4>
                <p className="text-slate-300 text-sm">
                    Zoom → Share Screen → "Entire Screen". Audience SEES everything: PowerPoint + open PromptNinja + your Chrome tabs. Embarrassing.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Share ONLY PowerPoint window. PromptNinja stays outside share = invisible to audience.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #2: Script Out of Sync with Slides (Wrong Order)</h4>
                <p className="text-slate-300 text-sm">
                    PowerPoint slide 10. Teleprompter script = text from slide 8. You speak old data while slide shows different graph. Audience confused.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> ALWAYS test BEFOREHAND. Advance slides manually while reading script = verify sync. Adjust [SLIDE X] markers.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #3: Reading "Like Robot" (No Tone Variation)</h4>
                <p className="text-slate-300 text-sm">
                    Use teleprompter but read monotone like GPS. Audience sleeps in 3min. You seem insincere even knowing content.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Mark EMPHASIS in script (CAPS, bold, **highlight**). Pause 2s at important commas. REHEARSE varying tone.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #4: Laptop/Tablet Too Far (Obvious Eye Movement)</h4>
                <p className="text-slate-300 text-sm">
                    Teleprompter 6 feet to the LEFT. You present turning head 60º to read. Audience KNOWS you're reading. Lose credibility.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Tablet/laptop MAXIMUM 1 foot from where you naturally look. In-person: beside podium. Online: below webcam.</span>
                </p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="font-bold text-red-400 mb-2">Error #5: No Backup if Laptop Crashes</h4>
                <p className="text-slate-300 text-sm">
                    Live presentation. Laptop crashes (Windows update?!). Teleprompter gone. You DIDN'T memorize = improvise HORRIBLY. Client notices.
                    <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> PromptNinja on phone too (QR code sync). Laptop crashes = grab phone discreetly, continue reading. Audience doesn't even notice.</span>
                </p>
            </div>
        </div>

        <h3 className="text-2xl font-bold text-white mt-8 mb-4">PowerPoint/Keynote Specific Tips</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">📊 Presenter View</h3>
                <p className="text-sm text-slate-300">PowerPoint/Keynote has "Presenter View": you see private notes, audience sees only slides. Combine WITH teleprompter = visual notes + complete script.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">🎨 Minimalist Slides</h3>
                <p className="text-sm text-slate-300">With teleprompter, slides can have ZERO text. Just impactful images. You speak rich from script, clean visual.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">⏱️ Auto-Advance Slides</h3>
                <p className="text-sm text-slate-300">PowerPoint can auto-advance slides every Xmin. COMBINE with [WAIT Xs] command in teleprompter = perfect automatic sync.</p>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-2">📱 Bluetooth Clicker</h3>
                <p className="text-sm text-slate-300">Clicker advances PPT slides (right hand). Phone controls teleprompter (left hand). Full control without touching laptop.</p>
            </div>
        </div>

        <div className="text-center bg-slate-800 p-8 rounded-lg my-10">
            <h3 className="text-3xl font-bold text-white mb-4">🎤 Memorable Presentations Without Memorizing</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Impactful slides + invisible script = confident presenter who "masters subject".
            </p>
            <a
                href="https://promptninja.solutionkit.com.br"
                className="inline-block bg-gradient-to-r from-orange-500 to-red-600 font-bold py-3 px-8 rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                🚀 Prepare Presentation Now (Free)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Sync with PowerPoint • Remote control • Invisible to audience</p>
        </div>

        <SEOContentFAQ
            title="Frequently Asked Questions - PowerPoint/Keynote Presentations"
            items={[
                {
                    question: "Will audience notice I'm reading from teleprompter?",
                    answer: "NO if you position correctly. Online: teleprompter below webcam = you look 'at camera'. In-person: laptop/tablet beside podium = look discreetly. Secret: don't FIX eyes on text 100% time. Look at audience, return to text, repeat naturally."
                },
                {
                    question: "Does it work with PowerPoint Presenter Mode?",
                    answer: "PERFECTLY! Presenter Mode shows: (1) current slide, (2) next slide, (3) private notes, (4) timer. PromptNinja = COMPLETE script on second screen. You have: slide visual + short notes + detailed script. Triple backup."
                },
                {
                    question: "How do I sync slides with teleprompter script?",
                    answer: "Use markers [SLIDE 1], [SLIDE 2] in BOLD in script. When you see [SLIDE 5], you advance PowerPoint manually (clicker or arrow). Or use [STOP] command = teleprompter pauses, you advance slide, resume reading."
                },
                {
                    question: "Can I use in Zoom meeting sharing screen?",
                    answer: "YES! Critical: share ONLY PowerPoint window (not Entire Screen). PromptNinja stays open outside share = you see, audience DOESN'T see. Test BEFORE: start Zoom, share, see preview = confirm teleprompter is invisible."
                },
                {
                    question: "Need internet during presentation or works offline?",
                    answer: "Works 100% OFFLINE after loaded. Auditorium presentation without WiFi = OK. Script saved locally. Only needs internet if: (1) first load or (2) phone remote control (uses local WiFi but can create hotspot)."
                },
                {
                    question: "How to prevent PowerPoint auto-dim screen during presentation?",
                    answer: "Windows: Settings → System → Power → Screen timeout = Never (during presentation). Mac: System Preferences → Energy Saver → disable sleep. Or keep laptop plugged in = usually prevents auto-dim."
                },
                {
                    question: "Can I use teleprompter for Q&A after slides?",
                    answer: "YES! Prepare separate script section: 'PREPARED FAQ'. List 10-15 likely questions + answers. During Q&A: search similar question in teleprompter (Ctrl+F), read prepared answer = seems improvised but is PERFECT."
                },
                {
                    question: "What if I want to improvise part of presentation (not read everything)?",
                    answer: "Script = GUIDE, not straitjacket. Mark [AD-LIB] sections where you improvise. Or use colors: green = read exact (critical statistics), yellow = paraphrase freely. Teleprompter = safety net, not prison."
                }
            ]}
        />
    </>
);
